import { isUndefined } from 'lodash';
import { getEnvironment } from '../../Config/Environment';
import { sessionTokenRepository } from '../Repositories/SessionTokenRepository';
import Logger from '../Logger/Logger';
import HttpService from './HttpService';
import { ErrorCode, ThirtyOneError } from '../Error/ThirtyOneError';
import SsoService from './SsoService';

const MissingEnvMessage = 'No environment was set!\n\nPlease refresh the page and try again.';
const logger = Logger.Create('CustomCustomLeaderboardApiService');

/**
 * Handles the Custom Leaderboard Api operations.
 */
export default class CustomLeaderboardApiService
{
  /**
   * Make a GET request to the server
   * @param url URL to send the request to.
   */
  public static async get<T = object>(url: string): Promise<T>
  {
    const env = getEnvironment();
    if (isUndefined(env))
    {
      logger.error(MissingEnvMessage);
      return Promise.reject(MissingEnvMessage);
    }

    const fullUrl = env.leaderboardApiBase + url;
    return CustomLeaderboardApiService.makeRequestWithRefresh<T>(async () =>
    {
      const token = await sessionTokenRepository.getJwtToken();
      return HttpService.get<T>(fullUrl, token);
    });
  }

  /**
   * Make a POST request to the server
   * @param url URL to send the request to.
   * @param data The data.
   */
  public static async post<T = object>(url: string, data: object): Promise<T>
  {
    const env = getEnvironment();
    if (isUndefined(env))
    {
      logger.error(MissingEnvMessage);
      return Promise.reject(MissingEnvMessage);
    }

    const fullUrl = env.leaderboardApiBase + url;
    const jsonData = JSON.stringify(data);

    return CustomLeaderboardApiService.makeRequestWithRefresh<T>(async () =>
    {
      const token = await sessionTokenRepository.getJwtToken();
      return HttpService.post<T>(fullUrl, jsonData, token);
    });
  }

  /**
   * Make a PUT request to the server
   * @param url URL to send the request to.
   * @param data The data.
   */
  public static async put<T = object>(url: string, data: object): Promise<T>
  {
    const env = getEnvironment();
    if (isUndefined(env))
    {
      logger.error(MissingEnvMessage);
      return Promise.reject(MissingEnvMessage);
    }

    const fullUrl = env.leaderboardApiBase + url;
    const jsonData = JSON.stringify(data);

    return CustomLeaderboardApiService.makeRequestWithRefresh<T>(async () =>
    {
      const token = await sessionTokenRepository.getJwtToken();
      return HttpService.put<T>(fullUrl, jsonData, token);
    });
  }

  /**
   * Make a DELETE request to the server
   * @param url URL to send the request to.
   */
  public static async delete<T = object>(url: string): Promise<T>
  {
    const env = getEnvironment();
    if (isUndefined(env))
    {
      logger.error(MissingEnvMessage);
      return Promise.reject(MissingEnvMessage);
    }

    const fullUrl = env.leaderboardApiBase + url;
    return CustomLeaderboardApiService.makeRequestWithRefresh<T>(async () =>
    {
      const token = await sessionTokenRepository.getJwtToken();
      return HttpService.delete<T>(fullUrl, token);
    });
  }

  private static async makeRequestWithRefresh<T>(request: () => Promise<T>): Promise<T>
  {
    try
    {
      // Make the first request.
      return await request();
    }
    catch (error)
    {
      const thirty1Error = error as ThirtyOneError;

      // If the error is not unauthorised, continue as normal.
      if (thirty1Error.errorCode !== ErrorCode.Unauthorised)
      {
        return Promise.reject(error);
      }
    }

    // Refresh the token.
    logger.info('Refreshing token...');
    await SsoService.refreshToken();

    try
    {
      // Make the second request attempt.
      return await request();
    }
    catch (error)
    {
      const thirty1Error = error as ThirtyOneError;

      // If the error is not unauthorised, continue as normal.
      if (thirty1Error.errorCode !== ErrorCode.Unauthorised)
      {
        return Promise.reject(error);
      }

      logger.info('Clearing token repository...');
      sessionTokenRepository.clear();
      return Promise.reject(error);
    }
  }
}
